import { FilterItem, FilterList } from '@/components_v2/Filtersv3/filter-item';
import { FilterCalendar } from '@/components_v2/Filtersv3/reactive-filter-calendar';
import { MacroColumnFilter, MacroTableFilter } from '@/gql/types';
import { useFetchOptions } from '@/hooks/useFetchOptions';
import { useAutomationFlow } from '@/screens/Tickets/context/automationFlow';
import React from 'react';
import { useTranslation } from 'react-i18next';

export default function Filters() {
  const { t } = useTranslation();
  const { flow } = useAutomationFlow();
  const statusQuery = useFetchOptions({
    column: MacroColumnFilter.Status,
    table: MacroTableFilter.AutomationTickets,
    filters: {},
  });

  const usersQuery = useFetchOptions({
    column: MacroColumnFilter.FullName,
    table: MacroTableFilter.Users,
    first: 50,
    filters: {},
  });

  const localsQuery = useFetchOptions({
    column: MacroColumnFilter.Name,
    table: MacroTableFilter.Locals,
    first: 50,
    filters: {},
  });

  const automationStepsQuery = useFetchOptions({
    column: MacroColumnFilter.Name,
    table: MacroTableFilter.AutomationSteps,
    first: 50,
    skip: !flow,
    allowCorrespondingFilter: true,
    filters: flow
      ? {
          automation_flow: { automation_flow_id: [flow?.id] },
          automation_steps: { step_type: ['opening', 'progressing'] },
        }
      : {},
  });

  const onSearchUserNameFilter = React.useCallback((search: string) => {
    usersQuery.refetch({
      search,
    });
  }, []);

  const onSearchLocalNameFilter = React.useCallback((search: string) => {
    localsQuery.refetch({
      search,
    });
  }, []);

  const onSearchAutomationStepNameFilter = React.useCallback(
    (search: string) => {
      automationStepsQuery.refetch({
        search,
      });
    },
    [],
  );

  const fetchMoreUserNameFilter = React.useCallback(() => {
    usersQuery.fetchMore({
      variables: {
        after: usersQuery.pageInfo?.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          macroFilters: {
            ...fetchMoreResult.macroFilters,
            edges: [
              ...prev.macroFilters.edges,
              ...fetchMoreResult.macroFilters.edges,
            ],
          },
        };
      },
    });
  }, [usersQuery]);

  const fetchMoreLocalNameFilter = React.useCallback(() => {
    localsQuery.fetchMore({
      variables: {
        after: localsQuery.pageInfo?.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          macroFilters: {
            ...fetchMoreResult.macroFilters,
            edges: [
              ...prev.macroFilters.edges,
              ...fetchMoreResult.macroFilters.edges,
            ],
          },
        };
      },
    });
  }, [localsQuery]);

  const fetchMoreAutomationStepNameFilter = React.useCallback(() => {
    automationStepsQuery.fetchMore({
      variables: {
        after: automationStepsQuery.pageInfo?.endCursor,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          macroFilters: {
            ...fetchMoreResult.macroFilters,
            edges: [
              ...prev.macroFilters.edges,
              ...fetchMoreResult.macroFilters.edges,
            ],
          },
        };
      },
    });
  }, [automationStepsQuery]);

  return (
    <FilterList>
      <FilterItem
        label={t('filters.status')}
        filterKey="status"
        loading={statusQuery.loading}
        options={statusQuery.options}
      />
      <FilterItem
        label={t('filters.user')}
        filterKey="user_id"
        loading={usersQuery.loading}
        options={usersQuery.options}
        onSearch={onSearchUserNameFilter}
        hasMore={usersQuery.pageInfo?.hasNextPage}
        fetchMore={fetchMoreUserNameFilter}
      />
      <FilterItem
        label={t('filters.location')}
        filterKey="local_id"
        loading={localsQuery.loading}
        options={localsQuery.options}
        onSearch={onSearchLocalNameFilter}
        hasMore={localsQuery.pageInfo?.hasNextPage}
        fetchMore={fetchMoreLocalNameFilter}
      />
      <FilterItem
        label={t('filters.event')}
        filterKey="automation_step_ids"
        loading={automationStepsQuery.loading}
        options={automationStepsQuery.options}
        onSearch={onSearchAutomationStepNameFilter}
        hasMore={automationStepsQuery.pageInfo?.hasNextPage}
        fetchMore={fetchMoreAutomationStepNameFilter}
      />
      <FilterCalendar label={t('filters.creation_date')} filterKey="created_at" />
    </FilterList>
  );
}
